import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { trpc } from '@/utils/trpc';

interface TenantSelectorProps {
  selectedTenantId?: string;
  onTenantSelect: (tenantId: string | undefined) => void;
}

/**
 * Tenant Selector Component for SYSTEM_ADMIN
 *
 * Displays a dropdown of all available tenants for SYSTEM_ADMIN users
 * to select which tenant's statistics to view.
 */
const TenantSelector: React.FC<TenantSelectorProps> = ({ selectedTenantId, onTenantSelect }) => {
  const { t } = useTranslation();

  // Fetch all available tenants
  const {
    data: tenantsResponse,
    isLoading,
    error,
  } = trpc.tenant.list.useQuery(
    { limit: 100 }, // Fetch up to 100 tenants
    {
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false,
    }
  );

  // Handle loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <span>{t('insight.tenantSelector.loading')}</span>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return <Alert severity="error">{t('insight.tenantSelector.error')}</Alert>;
  }

  // Handle no tenants available
  const tenants = tenantsResponse?.items || [];
  if (tenants.length === 0) {
    return <Alert severity="info">{t('insight.tenantSelector.noTenants')}</Alert>;
  }

  return (
    <FormControl size="small" fullWidth>
      <InputLabel id="tenant-selector-label">{t('insight.tenantSelector.label')}</InputLabel>
      <Select
        labelId="tenant-selector-label"
        id="tenant-selector"
        value={selectedTenantId || ''}
        onChange={(e) => onTenantSelect(e.target.value || undefined)}
        label={t('insight.tenantSelector.label')}
      >
        <MenuItem value="">
          <em>{t('insight.tenantSelector.placeholder')}</em>
        </MenuItem>
        {tenants.map((tenant) => (
          <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
            {tenant.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default TenantSelector;
